import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { UserManageService } from "../../services/UserManageService";
import { RoleService } from "../../services/RoleService";
import { IMaskInput } from "react-imask";

const userManageService = new UserManageService();
const roleService = new RoleService();

export default function UserEdit() {
  const navigate = useNavigate();
  const { userId } = useParams(); // Retrieve userId from route params

  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    surname: "",
    lastName: "",
    email: "",
    cpf: "",
    phoneNumber: "",
    roleId: "",
    active: true,
  });

  const [roles, setRoles] = useState([]);
  const isEditMode = !!userId;

  useEffect(() => {
    if (isEditMode) {
      userManageService
        .findOne(userId)
        .then((user) => {
          setFormData({
            username: user.username || "",
            password: "",
            name: user.name || "",
            surname: user.surname || "",
            lastName: user.lastName || "",
            email: user.email || "",
            cpf: user.cpf || "",
            phoneNumber: user.phoneNumber || "",
            roleId: user.roleId || "",
            active: user.active ?? true,
          });
        })
        .catch((error) => {
          console.error("Erro ao buscar os dados do usuário:", error);
          alert("Falha ao carregar os detalhes do usuário.");
          navigate("/admin/users");
        });
    }

    // Fetch roles
    roleService
      .find()
      .then((response) => {
        const filteredRoles = response.items.filter(
          (role) => role.type !== "AUTHENTICATED"
        );
        setRoles(filteredRoles);
      })
      .catch((error) => {
        console.error("Erro ao buscar os papéis:", error);
        alert("Falha ao carregar os papéis de usuário.");
      });
  }, [userId, isEditMode, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Clean phone number - remove all non-numeric characters
    const cleanedFormData = {
      ...formData,
      phoneNumber: formData.phoneNumber.replace(/\D/g, ''),
      cpf: formData.cpf.replace(/\D/g, '') // Also clean CPF in case it has formatting
    };
    
    if (isEditMode) {
      userManageService
        .updateOne(userId, cleanedFormData)
        .then(() => {
          alert("Usuário atualizado com sucesso!");
          navigate("/admin/users");
        })
        .catch((error) => {
          console.error("Erro ao atualizar o usuário:", error);
          alert("Falha ao atualizar o usuário.");
        });
    } else {
      userManageService
        .create(cleanedFormData)
        .then(() => {
          alert("Usuário criado com sucesso!");
          navigate("/admin/users");
        })
        .catch((error) => {
          console.error("Erro ao criar o usuário:", error);
          alert("Falha ao criar o usuário.");
        });
    }
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">
          {isEditMode ? "Editar Usuário" : "Criar Usuário"}
        </h1>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={formData.active ?? true} // default true na criação
            onChange={() =>
              setFormData((prev) => ({ ...prev, active: !prev.active }))
            }
          />
          <label className="form-check-label ms-2">
            {formData.active ? "Ativo" : "Inativo"}
          </label>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Matrícula</label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Senha {isEditMode && "(deixe em branco para não alterar)"}</label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            className="form-control"
            required={!isEditMode}
            placeholder={isEditMode ? "Deixe em branco para manter a senha atual" : ""}
          />
        </div>
        <div className="alert alert-info mb-3">
          <small>
            <strong>Atenção:</strong> Para os campos de nome, utilize apenas letras, espaços, pontos e acentos. Números e outros caracteres especiais não são permitidos.
          </small>
        </div>
        <div className="mb-3">
          <label className="form-label">Nome</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Nome do Meio</label>
          <input
            type="text"
            name="surname"
            value={formData.surname}
            onChange={handleInputChange}
            className="form-control"
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Sobrenome</label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            className="form-control"
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Email</label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="form-control"
          />
        </div>
        <div className="mb-3">
          <label className="form-label">CPF</label>
          <input
            type="text"
            name="cpf"
            value={formData.cpf}
            onChange={handleInputChange}
            className="form-control"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Telefone</label>
          <IMaskInput
            mask="(00) 00000-0000"
            value={formData.phoneNumber}
            onAccept={(value) =>
              handleInputChange({ target: { name: "phoneNumber", value } })
            }
            className="form-control"
            placeholder="(00) 00000-0000"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Grupo</label>
          <select
            name="roleId"
            value={formData.roleId}
            onChange={handleInputChange}
            className="form-select"
            required
          >
            <option value="">Selecione um grupo</option>
            {roles.map((role) => (
              <option key={role._id} value={role._id}>
                {role.name}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" className="btn btn-primary">
          {isEditMode ? "Atualizar Usuário" : "Criar Usuário"}
        </button>
        <button
          type="button"
          className="btn btn-secondary ms-2"
          onClick={() => navigate("/admin/users")}
        >
          Cancelar
        </button>
      </form>
    </div>
  );
}
